import React, { useState, useCallback, useEffect, useRef } from 'react';
import type { RefObject } from 'react';
import { useIntl } from 'react-intl';

import Avatar from '@atlaskit/avatar/avatar';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import type { TriggerProps } from '@atlaskit/popup/types';
// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import { Popup } from '@atlaskit/popup/popup';
import { cssMap, cx } from '@atlaskit/css';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Text } from '@atlaskit/primitives/compiled/text';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { token } from '@atlaskit/tokens';

import { expValEquals } from '@confluence/feature-experiments';
import { LoadableLazy } from '@confluence/loadable';
import { usePageContentId } from '@confluence/page-context';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { withTransparentErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';
import { isInPreviewPanel } from '@confluence/preview-panel-api/entry-points/previewPanelUtils.ts';

import { i18n } from '../i18n.ts';
import type { UserShape } from '../ownership-models.ts';
import {
	withContentOwnershipContextProvider,
	useContentOwnershipContext,
} from '../ContentOwnershipContext.tsx';

import { getUserAvatarSrc } from './getUserAvatarSrc.ts';
import { useDuplicateRef } from './useDuplicateRef.ts';
import { CollaboratorsByline } from './CollaboratorsByline.tsx';

const LazyPopupContent = LoadableLazy({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-SpaceViewsMenuContent" */ './PageOwnershipByLineWithContributorsDropdownContent.tsx'
			)
		).PageOwnershipByLineWithContributorsDropdownContent,
});

const styles = cssMap({
	trigger: {
		borderRadius: token('radius.small'),
		paddingInlineStart: token('space.050'),
		paddingInlineEnd: token('space.050'),
		paddingBlockStart: token('space.025'),
		paddingBlockEnd: token('space.025'),
		backgroundColor: token('color.background.neutral.subtle'),
		textOverflow: 'ellipsis',
		whiteSpace: 'normal',
		transition: token('motion.button.hovered'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
			transition: token('motion.button.pressed'),
		},
	},
	smartFolderHeaderTrigger: {
		display: 'flex',
		alignItems: 'center',
		minHeight: '24px',
	},
	previewPanelCard: {
		paddingInlineStart: token('space.050'),
		paddingInlineEnd: token('space.050'),
		paddingBlockStart: token('space.025'),
		paddingBlockEnd: token('space.025'),
		textOverflow: 'ellipsis',
		whiteSpace: 'normal',
	},
});

type PageOwnershipByLineWithContributorsDropdownProps = {
	owner: UserShape | null;
	author: UserShape;
	contributorCountWithoutOwner: number;
	analyticsSource: string;
	generatedBy?: string | null;
	onOwnerChange?: (previousOwnerId: string, newOwnerId: string) => void;
	/**
	 * When false, the popup is rendered outside the DOM subtree of the trigger
	 * (via a portal), escaping any inherited colour scheme from a parent
	 * container (e.g. a folder header with a cover image). When omitted, the
	 * value is driven by the a11y experiment.
	 */
	shouldRenderToParent?: boolean;
	/** Applies folder-header-specific alignment styles used when this byline is rendered for a smart folder. */
	isSmartFolderHeaderByline?: boolean;
};
const PageOwnershipByLineWithContributorsDropdownComponent = ({
	owner,
	author,
	contributorCountWithoutOwner,
	analyticsSource,
	generatedBy,
	isSmartFolderHeaderByline = false,
	shouldRenderToParent: shouldRenderToParentProp,
}: PageOwnershipByLineWithContributorsDropdownProps) => {
	const intl = useIntl();
	const { openModal: openChangeOwnerModal } = useContentOwnershipContext();

	const [contentId] = usePageContentId();
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const [triggerRefDuplicate, setTriggerRefDuplicate] = useState<RefObject<HTMLElement> | null>(
		null,
	);
	const createDuplicateRef = useDuplicateRef();

	// Tracks whether the dropdown was explicitly opened by a click (pinned open).
	// When pinned, the dropdown stays open even when the mouse leaves.
	const isPinnedRef = useRef(false);

	// Reset state when changing contentId
	useEffect(() => {
		setIsDropdownOpen(false);
		isPinnedRef.current = false;
	}, [contentId]);

	const pageOwner = owner ?? author;
	const avatarSrc = getUserAvatarSrc(pageOwner);

	const profileCardResidesInPreviewPanel = isInPreviewPanel();

	const onPopupTriggerClick = useCallback(() => {
		// The click-to-open experiment forces a simple click toggle regardless of the hover feature.
		if (expValEquals('confluence_profile_card_consistent_updates', 'isEnabled', true)) {
			setIsDropdownOpen((prev) => !prev);
			return;
		}
		if (expValEquals('cc_show_byline_job_title', 'isEnabled', true)) {
			if (isDropdownOpen && !isPinnedRef.current) {
				// Menu is open from hover — pin it open instead of closing
				isPinnedRef.current = true;
				// already open — no state change needed
			} else {
				// Closed → open and pin, or pinned → close and unpin
				const next = !isDropdownOpen;
				isPinnedRef.current = next;
				setIsDropdownOpen(next);
			}
		} else {
			// Control path: simple toggle without pinning
			setIsDropdownOpen((prev) => !prev);
		}
	}, [isDropdownOpen]);

	// Hover-to-open logic: when cc_show_byline_job_title experiment is enabled,
	// the dropdown opens on hover instead of requiring a click (tooltip is also hidden).
	// A 200ms delay on mouse leave prevents the dropdown from closing while the user
	// moves their cursor from the trigger to the popup content.
	const hoverCloseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		return () => clearHoverCloseTimeout();
	}, []);

	const clearHoverCloseTimeout = () => {
		if (hoverCloseTimeoutRef.current) {
			clearTimeout(hoverCloseTimeoutRef.current);
			hoverCloseTimeoutRef.current = null;
		}
	};

	const startHoverCloseTimeout = () => {
		clearHoverCloseTimeout();
		hoverCloseTimeoutRef.current = setTimeout(() => {
			setIsDropdownOpen(false);
		}, 200);
	};

	// When the click-to-open experiment is on, the byline dropdown must only open on click, so the
	// hover-to-open behaviour from cc_show_byline_job_title is suppressed.
	const onPopupTriggerMouseEnter = () => {
		if (expValEquals('confluence_profile_card_consistent_updates', 'isEnabled', true)) {
			return;
		}
		if (expValEquals('cc_show_byline_job_title', 'isEnabled', true)) {
			clearHoverCloseTimeout();
			setIsDropdownOpen(true);
		}
	};

	const onPopupTriggerMouseLeave = () => {
		if (expValEquals('confluence_profile_card_consistent_updates', 'isEnabled', true)) {
			return;
		}
		if (!isPinnedRef.current && expValEquals('cc_show_byline_job_title', 'isEnabled', true)) {
			startHoverCloseTimeout();
		}
	};

	const onPopupContentMouseEnter = () => {
		clearHoverCloseTimeout();
	};

	const onPopupContentMouseLeave = () => {
		if (expValEquals('confluence_profile_card_consistent_updates', 'isEnabled', true)) {
			return;
		}
		if (!isPinnedRef.current && expValEquals('cc_show_byline_job_title', 'isEnabled', true)) {
			startHoverCloseTimeout();
		}
	};

	const onPopupClose = useCallback(() => {
		clearHoverCloseTimeout();
		isPinnedRef.current = false;
		setIsDropdownOpen(false);
	}, []);

	const onChangeOwnerClick = useCallback(() => {
		// accessibility: return focus to dropdown trigger when change owner modal closes - doesnt happen automatically because the change owner modal trigger is in the dropdown which gets unmounted when the modal opens.
		openChangeOwnerModal({ returnFocusRef: triggerRefDuplicate ?? undefined });
		setIsDropdownOpen(false);
	}, [openChangeOwnerModal, triggerRefDuplicate]);

	const collaboratorsByline =
		!analyticsSource.startsWith('folder') &&
		generatedBy !== 'ONBOARDING' &&
		expValEquals('dual_agent_avatar_card', 'isEnabled', true) ? (
			<CollaboratorsByline
				owner={pageOwner}
				fallbackContributorCount={contributorCountWithoutOwner}
			/>
		) : null;

	const ownerInfoInsidePreviewPanel = (
		<Inline space="space.050" alignBlock="center" xcss={styles.previewPanelCard}>
			{collaboratorsByline ?? (
				<>
					<Avatar src={avatarSrc} borderColor="transparent" size="xxsmall" />
					<Text size="small" color="color.text.subtle" maxLines={1}>
						{intl.formatMessage(
							generatedBy === 'ONBOARDING'
								? i18n.bylineMainTextCreatedByConfluence
								: i18n.abbreviatedBylineMainText,
							{ user: pageOwner.fullName },
						)}
					</Text>
				</>
			)}
		</Inline>
	);

	const popupTrigger = ({ ref, ...triggerProps }: TriggerProps) => {
		const triggerRef = createDuplicateRef(ref, setTriggerRefDuplicate);
		const pressable = (
			<Pressable
				testId="owner-with-contributors-dropdown-trigger"
				xcss={cx(styles.trigger, isSmartFolderHeaderByline && styles.smartFolderHeaderTrigger)}
				onClick={onPopupTriggerClick}
				onMouseEnter={onPopupTriggerMouseEnter}
				onMouseLeave={onPopupTriggerMouseLeave}
				ref={triggerRef}
				{...triggerProps}
			>
				{collaboratorsByline ?? (
					<Inline space="space.050" alignBlock="center">
						<Avatar src={avatarSrc} borderColor="transparent" size="xxsmall" />
						<Text size="small" color="color.text.subtle" maxLines={1}>
							{intl.formatMessage(
								generatedBy === 'ONBOARDING'
									? i18n.bylineMainTextCreatedByConfluence
									: i18n.abbreviatedBylineMainText,
								{ user: pageOwner.fullName },
							)}
						</Text>
					</Inline>
				)}
			</Pressable>
		);

		// The click-to-open experiment keeps the tooltip so the trigger reads as clickable; the byline
		// job title feature otherwise renders the pressable without a tooltip.
		if (
			!expValEquals('confluence_profile_card_consistent_updates', 'isEnabled', true) &&
			expValEquals('cc_show_byline_job_title', 'isEnabled', true)
		) {
			return pressable;
		}

		return (
			<Tooltip
				content={
					isDropdownOpen
						? undefined
						: intl.formatMessage(
								analyticsSource.startsWith('folder')
									? i18n.ownerDropdownTooltip
									: i18n.ownerWithContributorsDropdownTooltip,
							)
				}
				position="bottom"
				hideTooltipOnClick
			>
				{pressable}
			</Tooltip>
		);
	};

	if (profileCardResidesInPreviewPanel) {
		return ownerInfoInsidePreviewPanel;
	}
	return (
		<Popup
			content={() => (
				<div
					role="presentation"
					onMouseEnter={onPopupContentMouseEnter}
					onMouseLeave={onPopupContentMouseLeave}
					onFocus={onPopupContentMouseEnter}
					onBlur={onPopupContentMouseLeave}
				>
					<LazyPopupContent
						analyticsSource={`${analyticsSource}-byline`}
						owner={pageOwner}
						onChangeOwnerClick={onChangeOwnerClick}
						contributorCountWithoutOwner={contributorCountWithoutOwner}
					/>
				</div>
			)}
			trigger={popupTrigger}
			isOpen={isDropdownOpen}
			onClose={onPopupClose}
			placement="bottom-start"
			shouldRenderToParent={
				shouldRenderToParentProp !== undefined
					? shouldRenderToParentProp
					: expValEquals('a11y-fixes-week2-may-2026', 'isEnabled', true)
			}
		/>
	);
};

export const PageOwnershipByLineWithContributorsDropdown: React.FC<
	PageOwnershipByLineWithContributorsDropdownProps & {
		onOwnerChange?: (previousOwnerId: string, newOwnerId: string) => void;
	}
> = withTransparentErrorBoundary({
	attribution: Attribution.PAGE_EXPERIENCES,
})(withContentOwnershipContextProvider(PageOwnershipByLineWithContributorsDropdownComponent));
